<template>
  <div class="product-count-chart-slot">
    <canvas ref="chartCanvas" class="frame"></canvas>
  </div>
</template>

<script>
import layoutMixin from "@/mixins/layoutMixin";
import Chart from "chart.js/auto";
import axios from "@axios";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "@vue/composition-api";
import useAppConfig from "@core/app-config/useAppConfig";

const REFRESH_INTERVAL = 5000;

export default {
  mixins: [layoutMixin],
  props: {
    data: {
      type: Object,
    },
  },
  setup() {
    const chartCanvas = ref(null);
    const { skin } = useAppConfig();
    const isDark = computed(() => skin.value === "dark");

    let chartInstance = null;
    let refreshTimer = null;
    let isFetching = false;

    const getChartOptions = () => {
      const textColor = isDark.value ? "#D0D2D6" : "#5E5873";
      const gridColor = isDark.value
        ? "rgba(255, 255, 255, 0.08)"
        : "rgba(94, 88, 115, 0.10)";

      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: 400,
          easing: "easeOutQuart",
        },
        interaction: {
          mode: "index",
          intersect: false,
        },
        scales: {
          x: {
            grid: {
              display: false,
              drawBorder: false,
            },
            ticks: {
              color: textColor,
              padding: 8,
            },
          },
          y: {
            beginAtZero: true,
            grace: "8%",
            ticks: {
              color: textColor,
              stepSize: 1,
              precision: 0,
              padding: 8,
            },
            grid: {
              color: gridColor,
              drawBorder: false,
              borderDash: [4, 4],
            },
          },
        },
        plugins: {
          legend: {
            position: "top",
            align: "start",
            labels: {
              color: textColor,
              boxWidth: 8,
              boxHeight: 8,
              usePointStyle: true,
              pointStyle: "circle",
              font: {
                size: 12,
                weight: "500",
              },
            },
          },
          tooltip: {
            backgroundColor: isDark.value ? "#161D31" : "#FFFFFF",
            titleColor: textColor,
            bodyColor: textColor,
            borderColor: gridColor,
            borderWidth: 1,
            cornerRadius: 8,
            padding: 12,
          },
        },
      };
    };

    const createChart = async (products = {}) => {
      await nextTick();
      if (!chartCanvas.value) return;

      chartInstance = new Chart(chartCanvas.value, {
        type: "bar",
        data: {
          labels: Object.keys(products),
          datasets: [{
            label: "# of Products",
            backgroundColor: "rgba(0, 207, 232, 0.75)",
            hoverBackgroundColor: "#00CFE8",
            borderColor: "#00CFE8",
            borderWidth: 1,
            borderRadius: 6,
            borderSkipped: false,
            data: Object.values(products),
          }],
        },
        options: getChartOptions(),
      });
    };

    const updateChart = async (products) => {
      if (!chartInstance) {
        await createChart(products);
        return;
      }

      chartInstance.data.labels = Object.keys(products);
      chartInstance.data.datasets[0].data = Object.values(products);
      chartInstance.options = getChartOptions();
      chartInstance.update();
    };

    const fetchData = async () => {
      if (isFetching) return;

      isFetching = true;
      try {
        const { data } = await axios.get("/v1/count_product/stream");
        await updateChart(data || {});
        return true;
      } catch {
        return false;
      } finally {
        isFetching = false;
      }
    };

    const destroyChart = () => {
      if (chartInstance) {
        chartInstance.destroy();
        chartInstance = null;
      }
    };

    watch(isDark, () => {
      if (chartInstance) {
        chartInstance.options = getChartOptions();
        chartInstance.update();
      }
    });

    onMounted(async () => {
      await fetchData();
      refreshTimer = window.setInterval(fetchData, REFRESH_INTERVAL);
    });

    onBeforeUnmount(() => {
      window.clearInterval(refreshTimer);
      destroyChart();
    });

    return {
      chartCanvas,
    };
  },
};
</script>

<style lang="scss" scoped>
.product-count-chart-slot {
  width: 100%;
  height: 100%;
  min-height: 220px;
  padding: 0.75rem;
}

.frame {
  width: 100% !important;
  height: 100% !important;
}
</style>
